import { Actions, Async, Dispatchers } from '@client/new/helpers/types';
import { getDispatchers } from '@client/new/helpers/types-utils';
import { trackSessionEvent } from '@services/session-tracker/sessionTracker';
import { useEffect, useState } from 'react';
import { ExtrasModel } from '../model/extras.model';
import { ContentPDF } from '../model/extras.domain';
import {
  defaultValues,
  toUpdateContentInput,
} from '../viewModel/extras.mappers';
import { v4 } from 'uuid';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { updatePDFInputSchema } from './pdf.schema';
import { toErrors } from '@client/new/pages/promotions/view-model/newDiscount.mappers';
import toast from 'react-hot-toast';
import { CreatePdfInput, UpdatePdfInput } from 'generated/graphql';
import { mixTrack } from '@services/mixpanel';
import { useKeepScrollPosition } from '@client/new/hooks/useKeepScrollPosition';
import {
  createFormRegister,
  createOnChangeHandler,
  ExtendedFormRegister,
  getFormMode,
} from '../viewModel/formHelpers';

type PDFActionMap = {
  PrepareCreate: { parentId: string };
  Edit: ContentPDF['value'];
  Create: File;
  Cancel: null;
  PrepareReupload: null;
  Reupload: File;
};

export type PDFVMAction = Actions<PDFActionMap>;

type PDFMode = 'none' | 'create' | 'edit' | 'reupload';

type PDFState = {
  parentId: string;
  mode: PDFMode;
  content: ContentPDF['value'];
  uploadProgress: number;
};

type PDFViewModelParams = {
  model: ExtrasModel;
};

export type PDFViewModel = PDFState &
  Dispatchers<PDFActionMap> & {
    register: ExtendedFormRegister<ContentPDF['value']>;
    errors: any;
    values: ContentPDF['value'];
    onChange: (name: keyof ContentPDF['value'], value: any) => void;
  };

export const usePDFViewModel = (p: PDFViewModelParams): Async<PDFViewModel> => {
  const [showErrors, setShowErrors] = useState<boolean>(false);

  const [state, setState] = useState<PDFState>({
    parentId: '',
    mode: 'none',
    content: { ...defaultValues['ContentPDF'] },
    uploadProgress: 0,
  });

  useKeepScrollPosition(state.mode, 'none');

  const form = useForm({
    reValidateMode: 'onBlur',
    defaultValues: defaultValues['ContentPDF'],
    resolver: zodResolver(updatePDFInputSchema),
  });

  useEffect(() => {
    if (state.content && Object.keys(state.content).length > 0)
      form.reset({ ...state.content }); // TODO
  }, [form, state.content, state.parentId]);

  const values = form.watch();

  const onSubmit = (value: ContentPDF['value']) => {
    p.model
      .update(
        state.content.id,
        toUpdateContentInput({ type: 'ContentPDF', value }),
      )
      .then(() => {
        setState((prev) => ({
          ...prev,
          content: {
            ...prev.content,
            ...value,
          },
        }));

        mixTrack({
          type: 'Extras Block Saved',
          payload: {
            title: value.title,
            type: 'ContentPDF',
            'video time': 0,
            'video aspect ratio': 'n/a',
            'Comments on': false,
          },
        });
      })
      .catch((error) => {
        console.error('Error updating PDF:', error);
        toast.error('Failed to update PDF content');
      });
  };

  const onError = () => {
    setShowErrors(true);
  };

  const register = createFormRegister({
    form,
    onSubmit,
    onError,
    showErrors,
    mode: getFormMode(state.mode),
  });

  const onChange = createOnChangeHandler({
    form,
    onSubmit,
    onError,
    mode: getFormMode(state.mode),
  });

  const dispatchers = getDispatchers<PDFActionMap>(
    _dispatch(setState, p.model),
  );

  return {
    type: 'fetched',
    value: {
      ...state,
      ...dispatchers,
      register,
      onChange,
      values,
      errors: showErrors ? toErrors(form.formState.errors) : null,
    },
  };
};

const _dispatch =
  (
    setState: React.Dispatch<React.SetStateAction<PDFState>>,
    model: ExtrasModel,
  ) =>
  (a: PDFVMAction) => {
    trackSessionEvent('ViewAction', a);
    switch (a.type) {
      case 'PrepareCreate':
        return setState((prev) => ({
          ...prev,
          parentId: a.payload.parentId,
          mode: 'create',
          uploadProgress: 0,
          content: { ...defaultValues['ContentPDF'] },
        }));

      case 'Edit':
        return setState((prev) => ({
          ...prev,
          uploadProgress: 0,
          content: a.payload,
          mode: 'edit',
        }));

      case 'Create':
        return onCreate(setState, model, a.payload);

      case 'PrepareReupload':
        return setState((prev) => ({
          ...prev,
          mode: 'reupload',
        }));

      case 'Reupload':
        return onReupload(setState, model, a.payload);

      case 'Cancel':
        return setState(onCancel);
    }
  };

const onCreate = (
  setState: React.Dispatch<React.SetStateAction<PDFState>>,
  model: ExtrasModel,
  file: File,
) => {
  const v = validatePDFFile(file);
  if (v) {
    toast.error('Invalid PDF. ' + v);
    return;
  }

  setState((state) => {
    const input: CreatePdfInput = {
      parentId: state.parentId,
      id: v4(),
      title: file.name,
      downloadable: false,
      isPublished: false,
      fileName: file.name,
    };

    const onProgress = (uploadProgress: number) =>
      setState((prev) => ({
        ...prev,
        uploadProgress,
        content: { ...prev.content, fileName: file.name },
      }));

    model
      .createPDF({ input, file, onProgress })
      .then((payload) => {
        mixTrack({
          type: 'PDF Upload',
        });
        return _dispatch(setState, model)({ type: 'Edit', payload });
      })
      .catch((error) => {
        // TODO: Handle error properly
        console.error('Error creating PDF:', error);
      });

    return state;
  });
};

const validatePDFFile = (file: File): string | null => {
  if (file.type !== 'application/pdf') {
    return 'File must be a PDF';
  }
  if (file.size > 30 * 1024 * 1024) {
    return 'File size must be less than 30MB';
  }
  return null;
};

const onReupload = (
  setState: React.Dispatch<React.SetStateAction<PDFState>>,
  model: ExtrasModel,
  file: File,
) => {
  const v = validatePDFFile(file);
  if (v) {
    toast.error('Invalid PDF. ' + v);
    return;
  }

  setState((state) => {
    const input: UpdatePdfInput = {
      title: state.content.title,
      downloadable: false,
      isPublished: false,
      fileName: file.name,
    };

    const onProgress = (uploadProgress: number) =>
      setState((prev) => ({
        ...prev,
        uploadProgress,
        content: { ...prev.content, fileName: file.name },
      }));

    mixTrack({
      type: 'Extras Block Saved',
      payload: {
        title: input.title as string,
        type: 'ContentPDF',
        'video time': 0,
        'video aspect ratio': 'n/a',
        'Comments on': false,
      },
    });

    model
      .updatePDF({ input, itemId: state.content.id, file, onProgress })
      .then((payload) => _dispatch(setState, model)({ type: 'Edit', payload }))
      .catch((error) => {
        console.error('Error reuploading PDF:', error);
      });

    return state;
  });
};

const onCancel = (prev: PDFState): PDFState => {
  const mode = prev.mode === 'reupload' ? 'edit' : 'none';
  return { ...prev, mode, uploadProgress: 0 };
};
